import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { EjournalSessionSelectors } from '../../state/ejournal-session.selectors';
import { EsealsConverterService } from '../../services/eseals-converter.service';
import { JournalPacketCardWrapperComponent } from '../journal-packet-card-wrapper/journal-packet-card-wrapper.component';
import { JournalPacketCardComponent } from '../journal-packet-card/journal-packet-card.component';
import { JournalSignerCardWrapperComponent } from '../journal-signer-card-wrapper/journal-signer-card-wrapper.component';
import { JournalSignerCardComponent } from '../journal-signer-card/journal-signer-card.component';
import { JournalEntryCardWrapperComponent } from '../journal-entry-card-wrapper/journal-entry-card-wrapper.component';
import { JournalEntryCardComponent } from '../journal-entry-card/journal-entry-card.component';
import { JournalSealCardComponent } from '../journal-seal-card/journal-seal-card.component';
import { Capacitor } from '@capacitor/core';
import { FinalizeBtnComponent } from '../finalize-btn/finalize-btn.component';

@Component({
  selector: 'et-journal-dashboard',
  imports: [
    JournalPacketCardWrapperComponent,
    JournalPacketCardComponent,
    JournalSignerCardWrapperComponent,
    JournalSignerCardComponent,
    JournalEntryCardWrapperComponent,
    JournalEntryCardComponent,
    JournalSealCardComponent,
    FinalizeBtnComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full flex flex-col gap-4',
  },
})
export class DashboardComponent {
  private static readonly TAB_COUNT = 3;
  private static readonly SWIPE_THRESHOLD_PX = 50;

  private readonly store = inject(Store);
  private readonly esealsConverter = inject(EsealsConverterService);

  private swipeStartX: number | null = null;
  private swipeStartY: number | null = null;

  activeTab = signal<number>(0);

  journal = this.store.selectSignal(EjournalSessionSelectors.getEjournal);
  signers = this.store.selectSignal(EjournalSessionSelectors.getSigners);
  signersLoading = this.store.selectSignal(
    EjournalSessionSelectors.getSignersLoading,
  );
  entries = this.store.selectSignal(EjournalSessionSelectors.getEntries);
  entriesLoading = this.store.selectSignal(
    EjournalSessionSelectors.getEntriesLoading,
  );
  notarizedAuditActions = this.store.selectSignal(
    EjournalSessionSelectors.getNotarizedAuditActions,
  );
  notarizedAuditLoaded = this.store.selectSignal(
    EjournalSessionSelectors.getNotarizedAuditLoaded,
  );
  pageNamesByNumber = this.store.selectSignal(
    EjournalSessionSelectors.getPageNamesByNumber,
  );

  isFinalized = computed(() => !!this.journal()?.isFinal);

  seals = computed(() => {
    const journal = this.journal();
    return this.esealsConverter.toSeals({
      entries: this.entries(),
      signers: this.signers(),
      notarizedActions: this.notarizedAuditActions(),
      packetId: journal?.packetId ?? '',
      eJournalPacketId: journal?.id ?? '',
      pageNamesByNumber: this.pageNamesByNumber(),
      auditActionsLoaded: this.notarizedAuditLoaded(),
      defaultFee: journal?.feeCharged ?? 0,
    });
  });

  cancelActEntries = computed(() =>
    this.entries().filter((e) => !!e.cancelActUtcDate),
  );

  /** True when draft has no seals and no cancel acts, or final has no entries. */
  noEntriesContent = computed(() => {
    if (this.isFinalized()) {
      return this.entries().length === 0;
    }
    return this.seals().length === 0 && this.cancelActEntries().length === 0;
  });

  /* Show eSeals on web only when packet is signed (on native, seals are always visible) */
  showESeals = computed(() => {
    if (Capacitor.isNativePlatform()) return true;
    const packet = this.store.selectSignal(
      EjournalSessionSelectors.getSelectedPacket,
    )();
    return packet?.packetStatus === 'Signed';
  });

  onSelectTab(tab: number) {
    if (
      tab < 0 ||
      tab >= DashboardComponent.TAB_COUNT ||
      tab === this.activeTab() ||
      (tab > 0 && !this.journal())
    ) {
      return;
    }

    this.activeTab.set(tab);
  }

  onPointerDown(event: PointerEvent) {
    if (event.pointerType === 'mouse') {
      return;
    }

    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
  }

  onPointerUp(event: PointerEvent) {
    if (this.swipeStartX == null || this.swipeStartY == null) {
      return;
    }

    const dx = event.clientX - this.swipeStartX;
    const dy = event.clientY - this.swipeStartY;
    this.clearSwipeStart();

    if (
      Math.abs(dx) < DashboardComponent.SWIPE_THRESHOLD_PX ||
      Math.abs(dx) < Math.abs(dy)
    ) {
      return;
    }

    if (dx < 0) {
      this.onSelectTab(this.activeTab() + 1);
    } else {
      this.onSelectTab(this.activeTab() - 1);
    }
  }

  onPointerCancel() {
    this.clearSwipeStart();
  }

  translateStyle(): string {
    return `translateX(-${this.activeTab() * 100}%)`;
  }

  private clearSwipeStart() {
    this.swipeStartX = null;
    this.swipeStartY = null;
  }
}
